import { motion } from "framer-motion";
import {
  Check,
  BookOpen,
  Sparkles,
  ClipboardList,
  Video,
  ListChecks,
  LineChart,
} from "lucide-react";
import { PurchaseButton } from "./PurchaseButton";
import usePurchase, { PRODUCTS } from "@/hooks/usePurchase";

const FOUNDATION = {
  id: PRODUCTS.FOUNDATION,
  eyebrow: "Foundation Guide",
  title: "Day Trading Made Simple",
  subtitle: "Foundation Guide",
  price: "$50",
  tagline: "For beginners who want to learn the language first.",
  quote:
    "\"I don't know what candles, entries, stop losses, or chart levels mean yet — I need a simple beginner roadmap before I practice.\"",
  promise:
    "By the end, you'll understand the basic terms, read a simple chart, know why risk matters, and feel ready to begin practicing safely.",
  features: [
    { icon: BookOpen, text: "Master essential trading terminology & market basics" },
    { icon: LineChart, text: "Read candlesticks, support & resistance with confidence" },
    { icon: LineChart, text: "Understand risk — and why it matters more than entries" },
    { icon: ListChecks, text: "Start on a $100,000 paper trading account — practice risk-free from day one" },
    { icon: BookOpen, text: "Instant PDF · read on any device · lifetime access" },
  ],
  cta: "Get the Foundation Guide",
  testId: "foundation",
};

const ACTION = {
  id: PRODUCTS.ACTION,
  eyebrow: "Action Bundle · Most Popular",
  title: "Day Trading Made Simple",
  subtitle: "Action Bundle",
  price: "$95",
  tagline: "For beginners who want the guide plus practical tools to start.",
  quote:
    "\"I want someone to show me what to do next — checklists, templates, and help placing my first demo order.\"",
  promise:
    "By the end, you'll not only understand the basics — you'll have a repeatable practice routine, a trading plan, daily checklist, demo tracker, and video guidance for your first steps.",
  features: [
    { icon: Sparkles, text: "Everything in the Foundation Guide, included" },
    { icon: ClipboardList, text: "Trading plan template + daily checklist so nothing gets missed" },
    { icon: Video, text: "Video walkthroughs: setting up GBP/USD, timeframes, first demo order" },
    { icon: LineChart, text: "Demo tracker to log & review every paper trade" },
    { icon: ListChecks, text: "A repeatable daily routine you can follow every morning" },
    { icon: BookOpen, text: "Instant access · all future bundle updates included" },
  ],
  cta: "Get the Action Bundle",
  testId: "action",
  featured: true,
};

function TierCard({ tier }) {
  const { purchase, isLoading } = usePurchase();
  const loading = isLoading(tier.id);
  const featured = !!tier.featured;

  return (
    <motion.div
      data-testid={`pricing-card-${tier.testId}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className={`relative flex flex-col rounded-[2rem] p-8 md:p-10 overflow-hidden ${
        featured
          ? "bg-[#080808] border border-emerald-500/40 glow-emerald"
          : "bg-[#0a0a0a] border border-white/10"
      }`}
    >
      {featured && (
        <>
          <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl" />
        </>
      )}

      <div className="relative">
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-widest uppercase font-display font-semibold mb-6 ${
            featured
              ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
              : "bg-white/5 border border-white/10 text-white/60"
          }`}
        >
          {tier.eyebrow}
        </div>

        <h3 className="font-display font-extrabold text-white text-2xl md:text-3xl tracking-tight leading-tight">
          {tier.title}
        </h3>
        <div
          className={`mt-1 font-display font-bold text-xl md:text-2xl ${
            featured ? "text-emerald-400" : "text-white/70"
          }`}
        >
          {tier.subtitle}
        </div>

        <div className="mt-6 flex items-baseline gap-2">
          <span
            data-testid={`pricing-amount-${tier.testId}`}
            className={`font-display font-extrabold text-white text-6xl md:text-7xl tracking-tighter ${
              featured ? "text-glow-emerald" : ""
            }`}
          >
            {tier.price}
          </span>
          <span className="text-white/40 text-sm">USD · one-time</span>
        </div>

        <p className="mt-5 text-white/70 text-sm md:text-base leading-relaxed">
          {tier.tagline}
        </p>

        <div
          className={`mt-5 rounded-xl p-4 text-sm italic leading-relaxed ${
            featured
              ? "bg-emerald-500/5 border border-emerald-500/20 text-emerald-100/80"
              : "bg-white/[0.03] border border-white/10 text-white/60"
          }`}
        >
          {tier.quote}
        </div>

        <ul className="mt-6 space-y-3">
          {tier.features.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-white/80 text-sm md:text-[15px]"
            >
              <span
                className={`shrink-0 w-6 h-6 rounded-full grid place-items-center mt-0.5 ${
                  featured
                    ? "bg-emerald-500/20 border border-emerald-500/40"
                    : "bg-white/5 border border-white/10"
                }`}
              >
                <Check
                  className={`w-3.5 h-3.5 ${
                    featured ? "text-emerald-400" : "text-white/70"
                  }`}
                />
              </span>
              {f.text}
            </li>
          ))}
        </ul>

        <div
          className={`mt-7 rounded-xl p-4 text-xs md:text-sm leading-relaxed border-l-2 ${
            featured
              ? "border-emerald-400 bg-emerald-500/[0.04] text-white/70"
              : "border-white/25 bg-white/[0.02] text-white/60"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest font-display font-semibold text-white/40 mb-1.5">
            You'll walk away with
          </div>
          {tier.promise}
        </div>

        <div className="mt-8">
          {featured ? (
            <PurchaseButton
              testId={`pricing-purchase-btn-${tier.testId}`}
              onClick={() => purchase(tier.id)}
              loading={loading}
              size="lg"
              fullWidth
              label={tier.cta}
            />
          ) : (
            <button
              data-testid={`pricing-purchase-btn-${tier.testId}`}
              onClick={() => purchase(tier.id)}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 font-display font-bold uppercase tracking-[0.12em] text-sm rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-emerald-500/40 transition-all disabled:opacity-70 disabled:cursor-wait"
            >
              {loading ? "Redirecting..." : tier.cta}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/10 blur-[140px] -z-10"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-semibold font-display mb-4">
            Pricing
          </div>
          <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            Pick your <span className="text-emerald-400">starting point.</span>
          </h2>
          <p className="mt-5 text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Start with the fundamentals — or go straight to the full toolkit with checklists,
            templates, and video walkthroughs.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-stretch">
          <TierCard tier={FOUNDATION} />
          <TierCard tier={ACTION} />
        </div>

        <div className="mt-10 text-center text-xs text-white/40">
          Instant digital access · 30-day money-back guarantee · Secure Stripe checkout
        </div>
      </div>
    </section>
  );
}
